import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSettings } from '../../../context/settings-context';
import type { SalesAgentsAvailability } from '../types/sales-agent';
import { useCustomersApi } from './use-customers-api';

const QK_SALES_AGENTS_USED_COUNT = () => ['sales-agents', 'used'] as const;

export function useSalesAgentsAvailability() {
  const { customersApi } = useCustomersApi();
  const settings = useSettings();

  const { data, ...restQuery } = useQuery({
    queryKey: QK_SALES_AGENTS_USED_COUNT(),
    queryFn: () => customersApi.salesAgentsUsedCount(),
  });

  // Compute the availability based on settings and the result of the sales agents used count
  const availability = useMemo<SalesAgentsAvailability | undefined>(() => {
    if (!settings || !data) return undefined;

    const allowedSalesAgents = settings.allowedSalesAgents;

    if (allowedSalesAgents === undefined || allowedSalesAgents === null) {
      return undefined;
    }

    const { total: used } = data;

    return {
      used,
      max: allowedSalesAgents,
      remaining: allowedSalesAgents - used,
    };
  }, [settings, data]);

  return {
    ...restQuery,
    data: availability,
  };
}
